import { Service } from "@turizi/shared";
import { ServiceTitle } from "./services/service-title";
import { applyDiscount, formatDayShort, formatPrice, todayStart } from "@/lib/format-utils";
import { getServiceSellCost, getServiceSellPrice } from "@/lib/models-utils";
import { TagIcon } from "@/lib/tag-utils";
import { useTranslation } from "react-i18next";
import { useState, memo, useMemo } from "react";
import ServiceNewCost from "./prices/new-cost-section";
import { Image, ActionIcon } from "@mantine/core";
import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { useUpdateService } from "@/hooks/api-hooks";


interface Props {
    service: Service;
    onSelect?: (service: Service) => void;
    start_day?: string; // used for pricing, availability, etc.
    end_day?: string; // used for pricing, availability, etc.
    className?: string;
    showProviders?: boolean;
    providerId?: string;
    showSoldBy?: boolean;
    hideIconText?: boolean;
    iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    hidePrices?: boolean;
    hideMenu?: boolean;
    discount?: number | undefined
}


const ServiceCardPrivate = memo(function ServiceCardPrivate({
    service,
    onSelect,
    start_day = todayStart(),
    end_day,
    className,
    showProviders = false,
    providerId = null,
    showSoldBy = false,
    hideIconText = true,
    iconSize = 'xs',
    hidePrices = false,
    hideMenu = false,
    discount = undefined
}: Props) {
    const { t } = useTranslation();
    const [showNewCost, setShowNewCost] = useState(false);
    const updateService = useUpdateService();

    if (!service) {
        return null;
    }

    const sortedImages = [...(service.images || [])].sort((a, b) => a.order - b.order);
    const price = getServiceSellPrice(service, todayStart())?.[0];
    const cost = getServiceSellCost(service, todayStart())?.[0];

    return (
        <div
            key={service.id}
            onClick={(e) => {
                e.stopPropagation();
                onSelect?.(service);
            }}
            className={`${className || 'solid-box-border-hover'} relative group`}>

            {/* <ActionIcon
                variant="transparent"
                className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleToggleFavorite}
            >
                {service.is_favorite ? (
                    <IconStarFilled className="text-yellow-400" size={20} />
                ) : (
                    <IconStar className="text-gray-400 hover:text-yellow-400" size={20} />
                )}
            </ActionIcon> */}

            {/* {service.is_favorite ? (
                <IconStarFilled className="text-yellow-400" size={20} />
            ) : (
                <IconStar className="text-gray-400 hover:text-yellow-400" size={20} />
            )} */}

            {showNewCost && (
                <ServiceNewCost
                    service={service}
                    onSave={() => setShowNewCost(false)}
                    onCancel={() => setShowNewCost(false)}
                />
            )}

            <div className="flex items-start justify-between gap-1">
                <div className="flex items-start gap-2 w-full">
                    <div className="flex flex-col items-center gap-2">
                        {sortedImages?.[0] ? (
                            <Image
                                src={sortedImages[0].url}
                                radius="md"
                                w="auto"
                                h={150}
                                fit="cover"
                                className="border border-gray-200"
                            />
                        ) : (
                            <TagIcon
                                name={service.category}
                                hideText={hideIconText}
                                size={iconSize}
                                className="font-bold"
                            />
                        )}
                        <div className="text-gray-400 text-xxs text-center">
                            <span className="text-gray-400 text-[0.5rem]">#</span>{service.id}
                        </div>
                    </div>
                    <ServiceTitle service={service} hideProviders={!showProviders} links={!onSelect} />
                </div>
                {!hidePrices && (
                    <div className="flex gap-2 justify-between w-fit">
                        {cost ? (
                            <div className="text-right flex flex-col gap-1 solid-box-border flex-1 whitespace-nowrap">
                                <div className="text-gray-500 text-xs font-light">{t('cost')}: {t(`price_by_${service.price_by}`)}</div>
                                <div className="flex items-baseline justify-end gap-1">
                                    <div className="text-base font-semibold">{formatPrice(cost?.default_amount)} <span className="text-gray-400">{cost?.default_currency}</span></div>
                                </div>
                                <div className="text-gray-500 text-xxs font-medium italic">
                                    {formatDayShort(cost?.start_day)}{cost?.end_day && ` - ${formatDayShort(cost?.end_day)}`}
                                </div>
                            </div>
                        ) : null}
                        {(price && discount === undefined) ? (
                            <div className="text-right flex flex-col gap-1 solid-box-border-blue flex-1 whitespace-nowrap">
                                <div className="text-gray-500 text-xs font-light">{t('price')}: {t(`price_by_${service.price_by}`)}</div>
                                <div className="flex items-baseline justify-end gap-1">
                                    <div className="text-base font-semibold">{formatPrice(price?.default_amount)} <span className="text-gray-400">{price?.default_currency}</span></div>
                                </div>
                                <div className="text-gray-500 text-xxs font-medium italic">
                                    {formatDayShort(price?.start_day)}{price?.end_day && ` - ${formatDayShort(price?.end_day)}`}
                                </div>
                            </div>
                        ) : null}
                        {(price && discount !== undefined) ? (
                            <div className="text-right flex flex-col gap-1 solid-box-border-blue flex-1 whitespace-nowrap bg-blue-50/30">
                                <div className="text-gray-500 text-xs font-light">{t('price')}: {t(`price_by_${service.price_by}`)}</div>
                                <div className="flex flex-col items-end gap-0.5">
                                    <div className="text-xs text-gray-400 italic">
                                        {formatPrice(price?.default_amount)} {price?.default_currency}
                                    </div>
                                    <div className="flex items-baseline justify-end gap-2">
                                        <div className="text-red-600 font-bold text-xs bg-red-50 px-1.5 py-0.5 rounded">
                                            -{discount}%
                                        </div>
                                        <div className="text-base font-semibold">
                                            {formatPrice(applyDiscount(price?.default_amount, discount))} <span className="text-gray-400">{price?.default_currency}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-gray-500 text-xxs font-medium italic">
                                    {formatDayShort(price?.start_day)}{price?.end_day && ` - ${formatDayShort(price?.end_day)}`}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            {!hidePrices && (
                <div className="text-gray-400 text-xxs flex justify-end mt-2">
                    {t('availability')}: <span className="font-semibold">{service?.availability?.daily_available}</span>
                </div>
            )}
            {/* {showSoldBy && (
                <div className="flex items-center gap-2">
                    <div className="text-gray-400 text-xxs">
                        {t('sold_by')}
                    </div>
                    <div className="text-gray-400 text-xxs">
                        {service.seller?.name}
                    </div>
                </div>
            )} */}
        </div>
    );
});

export default ServiceCardPrivate;